import {
  createSlice,
  createAsyncThunk,
  createAction,
  createSelector,
} from '@reduxjs/toolkit';
import { Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import { fetchProjectsList, Project } from 'api/projectsList';
import { fetchFacilitatorsList } from 'api/facilitatorsList';
import {
  EventData,
  ScheduleProjectResource,
  NewScheduleCreationParams,
  ScheduleProjectOption,
  Facilitator,
  ScheduleUpdateParams,
  ProjectResource,
  ExceptionCreationParams,
  SearchFilterParams,
  ScheduleFacilitatorResource,
  FacilitatorResource,
  FacilitatorsSearchFilterParams,
  MyScheduleSearchFilterParams,
} from 'utils/types/learningSchedule';
import {
  serializeEvents,
  serializeCreatedEvent,
  serializeExceptionEvent,
  isTheCurrentUserAFacilitator,
} from './helpers';
import { RootState } from 'state/store';
import ScheduleAPI from './ScheduleAPI';
import { EventFilters } from 'utils/types/filters';
import { View } from '@syncfusion/ej2-react-schedule';
import Holidays from 'date-holidays';
import { v4 as uuidv4 } from 'uuid';
export interface AvailableProgram {
  id: string;
  name: string;
  displayId: string;
}

interface ScheduleState {
  status: Status;
  value: EventsValue;
  projects: ProjectResource[];
  holidays: EventData[];
  facilitators: FacilitatorResource[];
  facilitatorsForDropdown: Facilitator[];
  projectsForDropdown: Project[];
  programsForDropdown: AvailableProgram[];
  search: {
    events: string;
    facilitators: string;
    mySchedule: string;
  };
  currentView: {
    events: View;
    facilitators: View;
    mySchedule: View;
  };
  filters: {
    events: EventFilters;
    facilitators: EventFilters;
  };
}

type EventsValue = {
  events: EventData[];
  mySchedule: EventData[];
  facilitatorEvents: EventData[];
};

export const DUMMY_PROJECT_ID = '00000000-0000-0000-0000-000000000000';
const DUMMY_PROJECT = {
  id: DUMMY_PROJECT_ID,
  title: 'No Project',
  status: 'DummyStatus',
};
/* ============================= INITIAL STATE ============================== */

const generateHolidays = (
  year: number = new Date().getFullYear(),
  countryCode: string
) => {
  const holidays = new Holidays();
  const country = countryCode;
  holidays.init(country);
  const holidayCountries = holidays.getCountries();
  const countryLabel = holidayCountries[countryCode];
  const allHolidays = holidays.getHolidays(year);
  const filteredHolidays = allHolidays.filter(
    (holiday) => holiday.type === 'public'
  );
  const serializedHolidays = filteredHolidays.map((holiday) => ({
    id: uuidv4(),
    subject: holiday.name,
    startTime: new Date(holiday.date).toISOString(),
    endTime: new Date(holiday.date).toISOString(),
    projectIds: [],
    learningEventType: '1',
    isAllDay: true,
    recurrenceRule: '',
    projects: [],
    facilitators: [],
    facilitatorIds: [],
    isOldEvent: false,
    holidayCountry: countryLabel,
  }));
  return serializedHolidays;
};

const generateHolidaysForFacilitators = (
  facilitators: FacilitatorResource[],
  year: number = new Date().getFullYear()
) => {
  const uniqueCountryCodes = [
    ...new Set(facilitators.map((fac) => fac.country)),
  ];

  return uniqueCountryCodes.flatMap((countryCode: string | 'US') => {
    const holidays = generateHolidays(year, countryCode);
    const facilitatorsFromCountry = facilitators.filter(
      (fac) => fac.country === countryCode
    );

    return holidays.map((holiday) => ({
      ...holiday,
      facilitators: facilitatorsFromCountry.map((fac) => ({
        id: fac.id,
        name: fac.name,
      })),
      facilitatorIds: facilitatorsFromCountry.map((fac) => fac.id),
    }));
  });
};

const initialState: ScheduleState = {
  value: {
    events: [],
    mySchedule: [],
    facilitatorEvents: [],
  },
  holidays: [],
  status: SLICE_STATUS.IDLE,
  projects: [],
  facilitators: [],
  projectsForDropdown: [],
  programsForDropdown: [],
  search: {
    events: '',
    facilitators: '',
    mySchedule: '',
  },
  // CGP-207 invariant: events/facilitators must stay a declared, weekend-capable
  // ViewDirective ('TimelineWeek'). A WorkWeek variant ('TimelineWorkWeek')
  // makes Syncfusion's getRenderDates filter purely on isWorkDay and ignore
  // showWeekend, so the fmShowWeekends flag silently stops working on first
  // paint. Test fixtures derive from this initialState (testHelpers.tsx), so
  // regressing this value fails ScheduleCalendar's renderDates test.
  currentView: {
    events: 'TimelineWeek',
    facilitators: 'TimelineWeek',
    mySchedule: 'Week',
  },
  facilitatorsForDropdown: [],
  filters: {
    events: {},
    facilitators: {},
  },
};

const stripDummyProjectId = (projectIds: string[]) => {
  return projectIds.filter((id) => id !== DUMMY_PROJECT_ID);
};

/* ============================= ACTIONS ============================== */
export const updateEventsSearchTerm = createAction<{
  search: string;
}>('SCHEDULE/UPDATE_EVENTS_SEARCH_TERM');

export const updateMyScheduleSearchTerm = createAction<{
  search: string;
}>('SCHEDULE/UPDATE_MY_SCHEDULE_SEARCH_TERM');

export const updateFacilitatorsSearchTerm = createAction<{
  search: string;
}>('SCHEDULE/UPDATE_FACILITATORS_SEARCH_TERM');

export const updateEventsCurrentView = createAction<{
  currentView: View;
}>('SCHEDULE/UPDATE_EVENTS_SELECTED_VIEW');

export const updateFacilitatorsCurrentView = createAction<{
  currentView: View;
}>('SCHEDULE/UPDATE_FACILITATORS_SELECTED_VIEW');

export const updateMyScheduleCurrentView = createAction<{
  currentView: View;
}>('SCHEDULE/UPDATE_MY_SCHEDULE_SELECTED_VIEW');

export const setFilters = createAction<EventFilters>('SCHEDULE/SET_FILTERS');
export const setEventsFilters = createAction<EventFilters>(
  'SCHEDULE/SET_EVENTS_FILTERS'
);

export const setFacilitatorsFilters = createAction<EventFilters>(
  'SCHEDULE/SET_FACILITATORS_FILTERS'
);

export const updateHolidaysForYear = createAction<{
  year: number;
  pageType: 'MY_SCHEDULE' | 'FACILITATORS';
  countryCode?: string;
  facilitators?: FacilitatorResource[];
}>('SCHEDULE/UPDATE_HOLIDAYS_FOR_YEAR');

/* ============================== REDUX THUNK =============================== */
export const createEvent = createAsyncThunk(
  'SCHEDULE/CREATE_EVENT',
  async (params: NewScheduleCreationParams, { getState }) => {
    const state = getState() as RootState;
    const currentUser = state.currentUser.value;
    const { ...apiParams } = params;
    const projectIds =
      apiParams.schedule.scheduleProjectIds &&
      stripDummyProjectId(apiParams.schedule.scheduleProjectIds);
    apiParams.schedule.scheduleProjectIds = projectIds;
    const response = await ScheduleAPI.createSchedule(apiParams);
    return {
      response: response.data,
      currentUserId: currentUser.id,
    };
  }
);

export const fetchProjects = createAsyncThunk(
  'SCHEDULE/FETCH_PROJECTS',
  async (projectName?: string) => {
    const response = await fetchProjectsList(projectName);
    return response;
  }
);

export const fetchProgramsForDropdown = createAsyncThunk(
  'SCHEDULE/FETCH_PROGRAMS',
  async (programName?: string) => {
    const response = await ScheduleAPI.fetchPrograms(programName);
    return response.availablePrograms;
  }
);

export const fetchFacilitatorsEvents = createAsyncThunk(
  'SCHEDULE/FETCH_FACILITATORS_EVENTS',
  async (params: FacilitatorsSearchFilterParams) => {
    const { optionalParams, ...apiParams } = params;
    const showPublicHolidays = optionalParams?.showPublicHolidays;
    const year = optionalParams?.year;
    const response = await ScheduleAPI.fetchFacilitatorsEvents(apiParams);
    return { ...response, showPublicHolidays, year };
  }
);

export const editEvent = createAsyncThunk(
  'SCHEDULE/EDIT_EVENT',
  async (params: ScheduleUpdateParams, { getState }) => {
    const state = getState() as RootState;
    const currentUser = state.currentUser.value;
    const { ...apiParams } = params;
    const scheduleId = apiParams.scheduleId || '';
    const scheduleProjectIds = stripDummyProjectId(
      apiParams.schedule.scheduleProjectIds || []
    );
    const updateParams = {
      learningEvent: apiParams.learningEvent,
      schedule: {
        ...apiParams.schedule,
        scheduleProjectIds,
      },
      resetExceptions: apiParams.resetExceptions,
    };
    const response = await ScheduleAPI.updateSchedule(scheduleId, updateParams);
    return {
      data: response.data,
      resetExceptions: apiParams.resetExceptions,
      currentUserId: currentUser.id,
    };
  }
);

export const createException = createAsyncThunk(
  'SCHEDULE/CREATE_EXCEPTION',
  async (params: ExceptionCreationParams, { getState }) => {
    const state = getState() as RootState;
    const currentUser = state.currentUser.value;
    const { ...apiParams } = params;
    const scheduleProjectIds = stripDummyProjectId(
      apiParams.data.schedule.scheduleProjectIds || []
    );
    apiParams.data.schedule.scheduleProjectIds = scheduleProjectIds;
    const response = await ScheduleAPI.createException(apiParams);
    return {
      response: response.data,
      currentUserId: currentUser.id,
    };
  }
);

export const fetchEvents = createAsyncThunk(
  'SCHEDULE/FETCH_EVENTS',
  async (params: SearchFilterParams) => {
    const { ...apiParams } = params;
    const response = await ScheduleAPI.fetchEvents(apiParams);
    return { ...response };
  }
);

export const fetchMySchedule = createAsyncThunk(
  'SCHEDULE/FETCH_MY_SCHEDULE',
  async (params: MyScheduleSearchFilterParams) => {
    const { optionalParams, ...apiParams } = params;
    const countryCode = optionalParams?.countryCode;
    const showPublicHolidays = optionalParams?.showPublicHolidays;
    const year = optionalParams?.year;
    const response = await ScheduleAPI.fetchMySchedule(apiParams);
    return { ...response, countryCode, showPublicHolidays, year };
  }
);

export const deleteEvent = createAsyncThunk(
  'SCHEDULE/DELETE_EVENT',
  async (scheduleId: string) => {
    const response = await ScheduleAPI.deleteSchedule(scheduleId);
    return response.data;
  }
);

export const fetchFacilitators = createAsyncThunk(
  'SCHEDULE/FETCH_FACILITATORS',
  async (params?: {
    search?: string;
    filters?: {
      languages: string[];
      skills: string[];
      countries: string[];
    };
  }) => {
    const response = await fetchFacilitatorsList(
      params?.search,
      params?.filters
    );
    return response;
  }
);

/* ================================= REDUCER ================================ */
const scheduleSlice = createSlice({
  name: 'schedule',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createEvent.fulfilled, (state, action) => {
        if (action.payload) {
          const schedules = action.payload.response;
          if (schedules.projects.length === 0) {
            const dummyProject = {
              ...DUMMY_PROJECT,
              schedules: [],
              isArchived: false,
            };
            schedules.projects = [dummyProject];
            schedules.projectIds = [DUMMY_PROJECT_ID];
          }
          const serializedEvent = serializeCreatedEvent(schedules);
          state.value.events.push(serializedEvent);
          state.value.facilitatorEvents.push(serializedEvent);
          if (
            isTheCurrentUserAFacilitator(
              serializedEvent,
              action.payload.currentUserId!
            )
          ) {
            state.value.mySchedule.push(serializedEvent);
          }
          state.status = SLICE_STATUS.IDLE;
        }
      })
      .addCase(createEvent.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(createEvent.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
        throw new Error();
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.projectsForDropdown = action.payload.projects;
      })
      .addCase(fetchFacilitators.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(fetchFacilitators.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(fetchFacilitators.fulfilled, (state, action) => {
        state.facilitatorsForDropdown = action.payload;
      })
      .addCase(editEvent.fulfilled, (state, action) => {
        if (action.payload) {
          const updatedEvent = action.payload.data;
          if (updatedEvent.projects.length === 0) {
            updatedEvent.projects = [DUMMY_PROJECT];
            updatedEvent.projectIds = [DUMMY_PROJECT_ID];
          }
          const serializedEvent = serializeCreatedEvent(updatedEvent);
          const index = state.value.events.findIndex(
            (event) => event.Id === serializedEvent.Id
          );
          const facilitatorEventIndex = state.value.facilitatorEvents.findIndex(
            (event) => event.Id === serializedEvent.Id
          );
          const myScheduleEventIndex = state.value.mySchedule.findIndex(
            (event) => event.Id === serializedEvent.Id
          );

          if (serializedEvent.RecurrenceID) {
            const parentEvent = state.value.events.find(
              (event) => event.Id === serializedEvent.RecurrenceID
            );
            if (parentEvent) {
              serializedEvent.RecurrenceRule = parentEvent.RecurrenceRule;
            }
          }
          if (index !== -1) {
            state.value.events[index] = serializedEvent;
          }
          if (facilitatorEventIndex !== -1) {
            state.value.facilitatorEvents[facilitatorEventIndex] =
              serializedEvent;
          }
          if (myScheduleEventIndex !== -1) {
            if (
              isTheCurrentUserAFacilitator(
                serializedEvent,
                action.payload.currentUserId!
              )
            ) {
              state.value.mySchedule[myScheduleEventIndex] = serializedEvent;
            } else {
              state.value.mySchedule = state.value.mySchedule.filter(
                (event) => event.Id !== serializedEvent.Id
              );
            }
          }
          if (action.payload.resetExceptions) {
            state.value.events = state.value.events.filter(
              (event) => event.RecurrenceID !== serializedEvent.Id
            );
            state.value.facilitatorEvents =
              state.value.facilitatorEvents.filter(
                (event) => event.RecurrenceID !== serializedEvent.Id
              );
            state.value.mySchedule = state.value.mySchedule.filter(
              (event) => event.RecurrenceID !== serializedEvent.Id
            );
          }

          state.status = SLICE_STATUS.IDLE;
        }
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        const formattedSchedules = action.payload.formattedSchedules;
        let projects = action.payload.projectsMatchingSearchParams;
        const schedulesWithoutProjects = formattedSchedules.filter(
          (schedule) => schedule.projectIds.length === 0
        );
        schedulesWithoutProjects.forEach((schedule) => {
          schedule.projectIds = [DUMMY_PROJECT_ID];
        });
        projects.push(DUMMY_PROJECT);

        state.value.events = serializeEvents(formattedSchedules);
        state.projects = projects;
      })
      .addCase(fetchMySchedule.fulfilled, (state, action) => {
        const showPublicHolidays = action.payload.showPublicHolidays;
        const countryCode = action.payload.countryCode;
        const year = action.payload.year || new Date().getFullYear();
        let formattedSchedules = action.payload.formattedSchedules;
        if (showPublicHolidays) {
          // Generate holidays for the year from calendar navigation
          const publicHolidays = generateHolidays(year, countryCode);
          formattedSchedules = formattedSchedules.concat(publicHolidays);
        }
        state.value.mySchedule = serializeEvents(formattedSchedules);
      })
      .addCase(updateEventsSearchTerm, (state, action) => {
        state.search.events = action.payload.search;
      })
      .addCase(updateMyScheduleSearchTerm, (state, action) => {
        state.search.mySchedule = action.payload.search;
      })
      .addCase(updateFacilitatorsSearchTerm, (state, action) => {
        state.search.facilitators = action.payload.search;
      })
      .addCase(updateEventsCurrentView, (state, action) => {
        state.currentView.events = action.payload.currentView;
      })
      .addCase(updateMyScheduleCurrentView, (state, action) => {
        state.currentView.mySchedule = action.payload.currentView;
      })
      .addCase(updateFacilitatorsCurrentView, (state, action) => {
        state.currentView.facilitators = action.payload.currentView;
      })
      .addCase(editEvent.rejected, () => {
        throw new Error();
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.value.events = state.value.events.filter((event) => {
          return (
            event.Id !== action.payload.deletedEventId &&
            event.RecurrenceID !== action.payload.deletedEventId
          );
        });
        state.value.facilitatorEvents = state.value.facilitatorEvents.filter(
          (event) => {
            return (
              event.Id !== action.payload.deletedEventId &&
              event.RecurrenceID !== action.payload.deletedEventId
            );
          }
        );
      })
      .addCase(deleteEvent.rejected, () => {
        throw new Error();
      })
      .addCase(setEventsFilters, (state, action) => {
        state.filters.events = action.payload || {};
      })
      .addCase(setFacilitatorsFilters, (state, action) => {
        state.filters.facilitators = action.payload || {};
      })
      .addCase(updateHolidaysForYear, (state, action) => {
        const { year, pageType, countryCode, facilitators } = action.payload;

        if (pageType === 'MY_SCHEDULE' && countryCode) {
          // Remove old holidays (events with holidayCountry property)
          const nonHolidayEvents = state.value.mySchedule.filter(
            (event) => !(event as any).holidayCountry
          );
          // Generate new holidays for the new year
          const newHolidays = generateHolidays(year, countryCode);
          const serializedHolidays = serializeEvents(newHolidays);
          // Merge with existing non-holiday events
          state.value.mySchedule = [...nonHolidayEvents, ...serializedHolidays];
        } else if (pageType === 'FACILITATORS' && facilitators) {
          // Remove old holidays (events with holidayCountry property)
          const nonHolidayEvents = state.value.facilitatorEvents.filter(
            (event) => !(event as any).holidayCountry
          );
          // Generate new holidays for the new year
          const newHolidays = generateHolidaysForFacilitators(
            facilitators,
            year
          );
          const serializedHolidays = serializeEvents(newHolidays);
          // Merge with existing non-holiday events
          state.value.facilitatorEvents = [
            ...nonHolidayEvents,
            ...serializedHolidays,
          ];
        }
      })
      .addCase(createException.fulfilled, (state, action) => {
        if (action.payload) {
          if (
            action.payload.response.projects.find(
              (project: Project) => project.id === DUMMY_PROJECT_ID
            )
          ) {
            action.payload.response.projects.push(DUMMY_PROJECT);
          }
          const serializedException = serializeExceptionEvent(
            action.payload.response
          );
          const parentId = action.payload.response.parent.id;
          const parentException =
            action.payload.response.parent.recurrenceException;
          const parentIndex = state.value.events.findIndex(
            (event) => event.Id === parentId
          );
          const facilitatorEventIndex = state.value.facilitatorEvents.findIndex(
            (event) => event.Id === parentId
          );
          const myScheduleEventIndex = state.value.mySchedule.findIndex(
            (event) => event.Id === parentId
          );
          const parentEvent = state.value.events[parentIndex];
          if (parentEvent) {
            serializedException.RecurrenceRule = parentEvent.RecurrenceRule;
          }
          if (parentIndex !== -1) {
            state.value.events[parentIndex].RecurrenceException =
              parentException;
            state.value.events.push(serializedException);
          }
          if (facilitatorEventIndex !== -1) {
            state.value.facilitatorEvents[
              facilitatorEventIndex
            ].RecurrenceException = parentException;
            state.value.facilitatorEvents.push(serializedException);
          }
          if (myScheduleEventIndex !== -1) {
            state.value.mySchedule[myScheduleEventIndex].RecurrenceException =
              parentException;
            if (
              isTheCurrentUserAFacilitator(
                serializedException,
                action.payload.currentUserId!
              )
            ) {
              state.value.mySchedule.push(serializedException);
            } else {
              state.value.mySchedule = state.value.mySchedule.filter(
                (event) => event.Id !== serializedException.Id
              );
            }
          }
        }
      })
      .addCase(createException.rejected, () => {
        throw new Error();
      })
      .addCase(fetchFacilitatorsEvents.fulfilled, (state, action) => {
        const showPublicHolidays = action.payload.showPublicHolidays;
        const facilitators = action.payload.facilitatorsMatchingSearchParams;
        const year = action.payload.year || new Date().getFullYear();
        let formattedSchedules = action.payload.formattedSchedules;
        if (showPublicHolidays) {
          const publicHolidaysForAllCountries = generateHolidaysForFacilitators(
            facilitators,
            year
          );
          formattedSchedules = formattedSchedules.concat(
            publicHolidaysForAllCountries
          );
        }
        state.value.facilitatorEvents = serializeEvents(formattedSchedules);
        state.facilitators = facilitators;
      })
      .addCase(fetchProgramsForDropdown.fulfilled, (state, action) => {
        state.programsForDropdown = action.payload;
      });
  },
});

/* =============================== SELECTORS ================================ */

export const selectScheduleEvents = (state: RootState) =>
  state.schedule.value.events;

export const selectFacilitatorsEvents = (state: RootState) =>
  state.schedule.value.facilitatorEvents;

export const selectMyScheduleEvents = (state: RootState) =>
  state.schedule.value.mySchedule;

export const selectEventsSearchTerm = (state: RootState) =>
  state.schedule.search.events;

export const selectMyScheduleSearchTerm = (state: RootState) =>
  state.schedule.search.mySchedule;

export const selectFacilitatorsViewSearchTerm = (state: RootState) =>
  state.schedule.search.facilitators;

export const selectEventsCurrentView = (state: RootState) =>
  state.schedule.currentView.events;

export const selectMyScheduleCurrentView = (state: RootState) =>
  state.schedule.currentView.mySchedule;

export const selectFacilitatorsCurrentView = (state: RootState) =>
  state.schedule.currentView.facilitators;

export const selectScheduleResources = (
  state: RootState
): ScheduleProjectResource[] =>
  state.schedule.projects.map((project) => ({
    ProjectId: project.id,
    Title: project.title,
    Status: project.status,
  }));

export const selectFacilitatorsResources = (
  state: RootState
): ScheduleFacilitatorResource[] =>
  state.schedule.facilitators.map((facilitator) => ({
    FacilitatorId: facilitator.id,
    Name: facilitator.name,
    Status: facilitator.status,
  }));

export const selectProjectsForDropdown = (
  state: RootState
): ScheduleProjectOption[] =>
  state.schedule.projectsForDropdown.map((project) => ({
    id: project.id,
    title: project.title,
    status: project.status,
  }));

export const selectAllProjects = createSelector(
  [selectScheduleResources, selectProjectsForDropdown],
  (scheduleProjectsResources, dropdownProjects) => {
    const extraProjects = scheduleProjectsResources
      .filter(
        (project) =>
          project.ProjectId !== DUMMY_PROJECT_ID &&
          !dropdownProjects.find(
            (dropdownProject) => dropdownProject.id === project.ProjectId
          )
      )
      .map((project) => ({
        id: project.ProjectId,
        title: project.Title,
        status: project.Status,
      }));

    const allProjects = [...dropdownProjects, ...extraProjects];

    return allProjects.map(({ id, title, status }) => ({
      id,
      title,
      status,
    }));
  }
);

export const selectFacilitatorsForDropdown = (
  state: RootState
): Facilitator[] => state.schedule.facilitatorsForDropdown;

export const selectProgramsForDropdown = (state: RootState) =>
  state.schedule.programsForDropdown;

export const selectFacilitators = (state: RootState): FacilitatorResource[] =>
  state.schedule.facilitators;

export default scheduleSlice.reducer;
